/**
 * HTTP Client with Axios configuration for the Poultry Management System
 * Handles authentication, error handling, and request/response interceptors
 */

import axios from 'axios';
import { API_CONFIG, STORAGE_KEYS } from '../utils/constants';

/**
 * HTTP Client class for API communication
 */
class HttpClient {
  constructor() {
    console.log('🔧 HttpClient initialized with baseURL:', API_CONFIG.BASE_URL);
    this.axiosInstance = axios.create({
      baseURL: API_CONFIG.BASE_URL,
      timeout: API_CONFIG.TIMEOUT,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    });

    this.setupInterceptors();
  }

  /**
   * Set up request and response interceptors
   */
  setupInterceptors() {
    // Request interceptor for adding auth token
    this.axiosInstance.interceptors.request.use(
      config => {
        const token = this.getAuthToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Add request timestamp for debugging
        config.metadata = { startTime: new Date() };

        // Log request in development
        if (process.env.NODE_ENV === 'development') {
          console.log(`🚀 ${config.method?.toUpperCase()} ${config.url}`, {
            data: config.data,
            params: config.params,
            headers: config.headers,
            token: this.getAuthToken(),
            fullUrl: `${config.baseURL}${config.url}`
          });
        }

        return config;
      },
      error => {
        console.error('Request interceptor error:', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor for handling responses and errors
    this.axiosInstance.interceptors.response.use(
      response => {
        // Calculate request duration
        const duration = new Date() - response.config.metadata.startTime;

        // Log response in development
        if (process.env.NODE_ENV === 'development') {
          console.log(
            `✅ ${response.config.method?.toUpperCase()} ${response.config.url} (${duration}ms)`,
            {
              status: response.status,
              data: response.data,
            }
          );
        }

        return response;
      },
      async error => {
        const originalRequest = error.config;

        // Log error in development
        if (process.env.NODE_ENV === 'development') {
          console.error(
            `❌ ${originalRequest?.method?.toUpperCase()} ${originalRequest?.url}`,
            {
              status: error.response?.status,
              message: error.message,
              data: error.response?.data,
              fullUrl: `${originalRequest?.baseURL}${originalRequest?.url}`,
              requestData: originalRequest?.data,
              requestHeaders: originalRequest?.headers
            }
          );
        }

        // Handle 403 Forbidden - Access Denied
        if (error.response?.status === 403) {
          console.error('Access Denied (403): User does not have necessary permissions');
          // Dispatch custom event for access denied
          window.dispatchEvent(
            new CustomEvent('auth:access_denied', {
              detail: { 
                message: 'You do not have permission to perform this action',
                url: originalRequest?.url 
              },
            })
          );
          // Optional: redirect to unauthorized page
          // window.location.href = '/unauthorized';
        }

        // Handle 401 Unauthorized - Token expired
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            const refreshToken = this.getRefreshToken();
            if (refreshToken) {
              const newToken = await this.refreshAuthToken(refreshToken);
              if (newToken) {
                this.setAuthToken(newToken);
                originalRequest.headers.Authorization = `Bearer ${newToken}`;
                return this.axiosInstance(originalRequest);
              }
            }
          } catch (refreshError) {
            console.error('Token refresh failed:', refreshError);
            this.handleAuthenticationFailure();
            return Promise.reject(refreshError);
          }

          this.handleAuthenticationFailure();
        }

        // Handle network errors
        if (!error.response) {
          const networkError = {
            message: 'Network error - please check your connection',
            code: 'NETWORK_ERROR',
            originalError: error,
          };
          return Promise.reject(networkError);
        }

        // Handle server errors
        const apiError = this.formatApiError(error);
        return Promise.reject(apiError);
      }
    );
  }

  /**
   * Format API error for consistent error handling
   * @param {Object} error - Axios error object
   * @returns {Object} Formatted error object
   */
  formatApiError(error) {
    const { response } = error;

    // Debug logging for error response
    if (process.env.NODE_ENV === 'development') {
      console.error('API Error Response:', {
        status: response?.status,
        data: response?.data,
        responseMessage: response?.data?.responseMessage,
        message: response?.data?.message,
        errorCode: response?.data?.errorCode,
        code: response?.data?.code
      });
    }

    return {
      message: response?.data?.responseMessage || response?.data?.message || error.message || 'An error occurred',
      code: response?.data?.errorCode || response?.data?.code || `HTTP_${response?.status}`,
      status: response?.status,
      errors: response?.data?.errors || [],
      timestamp: new Date().toISOString(),
      originalError: error,
    };
  }

  /**
   * Handle authentication failure
   */
  handleAuthenticationFailure() {
    this.clearAuthTokens();

    // Dispatch custom event for auth failure
    window.dispatchEvent(
      new CustomEvent('auth:failure', {
        detail: { message: 'Authentication failed' },
      })
    );

    // Redirect to login if not already there
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }

  /**
   * Refresh authentication token
   * @param {string} refreshToken - Refresh token
   * @returns {Promise<string>} New access token
   */
  async refreshAuthToken(refreshToken) {
    try {
      // Use axios instance so Authorization header (current token) is included as backend expects
      const response = await this.axiosInstance.post('/auth/refresh');

      // Support both wrapped ApiResponse and plain response
      const payload = response?.data ?? response;
      const data = payload?.responseData ?? payload;
      const token = data?.token;
      const newRefreshToken = data?.refreshToken;

      if (newRefreshToken) {
        this.setRefreshToken(newRefreshToken);
      }

      return token;
    } catch (error) {
      console.error('Token refresh failed:', error);
      throw error;
    }
  }

  /**
   * Get authentication token from storage
   * @returns {string|null} Auth token
   */
  getAuthToken() {
    return localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
  }

  /**
   * Set authentication token in storage
   * @param {string} token - Auth token
   */
  setAuthToken(token) {
    localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
  }

  /**
   * Get refresh token from storage
   * @returns {string|null} Refresh token
   */
  getRefreshToken() {
    return localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
  }

  /**
   * Set refresh token in storage
   * @param {string} token - Refresh token
   */
  setRefreshToken(token) {
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, token);
  }

  /**
   * Clear all authentication tokens
   */
  clearAuthTokens() {
    localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER_DATA);
  }

  /**
   * Generic GET request
   * @param {string} url - Request URL
   * @param {Object} config - Axios config
   * @returns {Promise} Response data
   */
  async get(url, config = {}) {
    try {
      const response = await this.axiosInstance.get(url, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Generic POST request
   * @param {string} url - Request URL
   * @param {Object} data - Request data
   * @param {Object} config - Axios config
   * @returns {Promise} Response data
   */
  async post(url, data = {}, config = {}) {
    try {
      const response = await this.axiosInstance.post(url, data, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Generic PUT request
   * @param {string} url - Request URL
   * @param {Object} data - Request data
   * @param {Object} config - Axios config
   * @returns {Promise} Response data
   */
  async put(url, data = {}, config = {}) {
    try {
      const response = await this.axiosInstance.put(url, data, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Generic PATCH request
   * @param {string} url - Request URL
   * @param {Object} data - Request data
   * @param {Object} config - Axios config
   * @returns {Promise} Response data
   */
  async patch(url, data = {}, config = {}) {
    try {
      const response = await this.axiosInstance.patch(url, data, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Generic DELETE request
   * @param {string} url - Request URL
   * @param {Object} config - Axios config
   * @returns {Promise} Response data
   */
  async delete(url, config = {}) {
    try {
      const response = await this.axiosInstance.delete(url, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Upload file with progress tracking
   * @param {string} url - Upload URL
   * @param {FormData} formData - Form data with file
   * @param {Function} onProgress - Progress callback
   * @returns {Promise} Response data
   */
  async uploadFile(url, formData, onProgress = null) {
    try {
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      };

      if (onProgress) {
        config.onUploadProgress = progressEvent => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          onProgress(percentCompleted);
        };
      }

      const response = await this.axiosInstance.post(url, formData, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Download file
   * @param {string} url - Download URL
   * @param {string} filename - File name
   * @returns {Promise} Blob data
   */
  async downloadFile(url, filename = null) {
    try {
      const response = await this.axiosInstance.get(url, {
        responseType: 'blob',
      });

      // Create download link
      const blob = new Blob([response.data]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Cancel request using cancel token
   * @param {string} requestId - Request identifier
   */
  cancelRequest(requestId) {
    // Implementation for request cancellation
    // This would require maintaining a map of cancel tokens
    console.log(`Cancelling request: ${requestId}`);
  }

  /**
   * Get current axios instance
   * @returns {Object} Axios instance
   */
  getInstance() {
    return this.axiosInstance;
  }

  /**
   * Update base URL
   * @param {string} baseURL - New base URL
   */
  setBaseURL(baseURL) {
    this.axiosInstance.defaults.baseURL = baseURL;
  }

  /**
   * Set default headers
   * @param {Object} headers - Headers object
   */
  setDefaultHeaders(headers) {
    Object.assign(this.axiosInstance.defaults.headers, headers);
  }
}

// Create and export singleton instance
const httpClient = new HttpClient();

export default httpClient;
